var m = require('mithril');
var prop = require('mithril/stream');
var modal = require('../layout/components/modal');
var loadingModal = require('../layout/components/loadingModal');
var fileInput = require('../components/inputs/fileInput');
var loaders = require('../layout/components/loaders');
var helpModal = require('../help/helpModal');
var paginationWithSearch = require('../data/components/paginationWithSearch');
var ToggleSwitch = require('../components/toggleSwitch');
var downloadFile = require('../layout/components/downloadFile');
var io = require('../libs/socketio');
var importLimits = require('./treeTransferImportLimits');

var treeTransferModal = {};
var MAX_INLINE_PAYLOAD_PREVIEW_BYTES = 1024 * 1024;
var EXPORT_HELP_FILE_NAME = 'networkTreeTransferExport.md';
var IMPORT_HELP_FILE_NAME = 'networkTreeTransferImport.md';

var SUPPORTED_HOST_CLASSES = {
  BacnetServer: {
    label: 'BACnet Host',
    childClassName: 'BacnetDevice',
    childLabel: 'BACnet Device'
  },
  ModbusTcp: {
    label: 'Modbus TCP',
    childClassName: 'ModbusDevice',
    childLabel: 'Modbus Device'
  },
  RestHttp: {
    label: 'Rest HTTP',
    childClassName: 'RestResource',
    childLabel: 'REST Resource'
  },
  LontalkCnip: {
    label: 'LonTalk CNIP',
    childClassName: 'LontalkNetwork',
    childLabel: 'LonTalk Network'
  }
};

var HOST_IMPORT_GUIDANCE = {
  BacnetServer: 'BACnet Host page',
  ModbusTcp: 'Modbus TCP page',
  RestHttp: 'REST HTTP page',
  LontalkCnip: 'LonTalk CNIP page'
};

function getHostSpec(className) {
  return SUPPORTED_HOST_CLASSES[className] || null;
}

function sanitizeFilename(filename, fallback) {
  var safe = String(filename || '')
    .replace(/[^a-zA-Z0-9._-]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^-+|-+$/g, '');
  if (!safe) {
    safe = fallback || 'network-tree-export.json';
  }
  var lower = safe.toLowerCase();
  var hasJsonSuffix = lower.slice(-5) === '.json';
  var hasJsonGzipSuffix = lower.slice(-8) === '.json.gz';
  var hasGzipSuffix = lower.slice(-3) === '.gz';
  if (!hasJsonSuffix && !hasJsonGzipSuffix && !hasGzipSuffix) {
    safe += '.json';
  }
  return safe;
}

function uint8ArrayToBase64(bytes) {
  var chunkSize = 0x8000;
  var binary = '';
  for (var i = 0; i < bytes.length; i += chunkSize) {
    var chunk = bytes.subarray(i, i + chunkSize);
    binary += String.fromCharCode.apply(null, chunk);
  }
  return window.btoa(binary);
}

function gzipTextToBase64(text) {
  if (
    typeof CompressionStream === 'undefined' ||
    typeof Blob === 'undefined' ||
    typeof Response === 'undefined' ||
    typeof window === 'undefined' ||
    typeof window.btoa !== 'function'
  ) {
    return Promise.reject(new Error('Browser does not support gzip compression.'));
  }

  var stream = new Blob([text], { type: 'application/json' })
    .stream()
    .pipeThrough(new CompressionStream('gzip'));

  return new Response(stream)
    .arrayBuffer()
    .then(function (buffer) {
      return uint8ArrayToBase64(new Uint8Array(buffer));
    });
}

function readFileAsBase64(file) {
  if (!file) return Promise.resolve('');
  if (typeof file.arrayBuffer === 'function') {
    return file.arrayBuffer().then(function (buffer) {
      return uint8ArrayToBase64(new Uint8Array(buffer));
    });
  }
  return new Promise(function (resolve, reject) {
    var reader = new FileReader();
    reader.onload = function (event) {
      var result = event && event.target && event.target.result;
      if (!(result instanceof ArrayBuffer)) {
        reject(new Error('Could not read file.'));
        return;
      }
      resolve(uint8ArrayToBase64(new Uint8Array(result)));
    };
    reader.onerror = function () {
      reject(new Error('Could not read file.'));
    };
    reader.readAsArrayBuffer(file);
  });
}

function buildImportRequestBody(payloadText) {
  var payloadSize = new Blob([payloadText]).size;
  var sizeError = importLimits.validateByteLength(payloadSize, false);
  if (sizeError) {
    return Promise.reject(sizeError);
  }
  if (payloadSize <= MAX_INLINE_PAYLOAD_PREVIEW_BYTES) {
    return Promise.resolve({
      body: { payloadText: payloadText },
      compressed: false
    });
  }

  return gzipTextToBase64(payloadText)
    .then(function (gzipBase64) {
      return {
        body: {
          payloadEncoding: 'gzip-base64',
          payloadGzipBase64: gzipBase64
        },
        compressed: true
      };
    })
    .catch(function () {
      return {
        body: { payloadText: payloadText },
        compressed: false
      };
    });
}

function getExpectedBundleScopes(options) {
  var scopes = options && options.expectedBundleScopes;
  if (!Array.isArray(scopes) || !scopes.length) return null;
  return scopes.map(function (scope) {
    return String(scope || '').trim();
  }).filter(Boolean);
}

function validateImportBundleScope(payloadText, options) {
  var expectedScopes = getExpectedBundleScopes(options);
  if (!expectedScopes || !expectedScopes.length) return null;
  var parsed;
  try {
    parsed = JSON.parse(payloadText);
  } catch (_) {
    return null;
  }
  if (!parsed || typeof parsed !== 'object') return null;
  if (parsed.schema !== 'nimrod.network.tree.bundle') return null;
  var scope = String(parsed.scope || '').trim();
  if (!scope) return null;
  if (expectedScopes.indexOf(scope) >= 0) return null;
  if (scope === 'host' && expectedScopes.indexOf('network') >= 0) {
    var hostClassName = parsed && parsed.data && parsed.data.host ? parsed.data.host.className : '';
    var hostName = parsed && parsed.data && parsed.data.host ? parsed.data.host.name : '';
    var hostContext = hostName ? (' Host: "' + hostName + '".') : '';
    if (hostClassName && HOST_IMPORT_GUIDANCE[hostClassName]) {
      return 'This file is a host export (scope "host") for ' + hostClassName + '. Use Import Host Nodes from the ' + HOST_IMPORT_GUIDANCE[hostClassName] + '.' + hostContext;
    }
    return 'This file is a host export (scope "host"). Use Import Host Nodes from a specific host page.' + hostContext;
  }
  return 'Bundle scope "' + scope + '" is not supported here. Allowed scopes: ' + expectedScopes.join(', ') + '.';
}

function isGzipFile(file) {
  if (!file) return false;
  var name = String(file.name || '').toLowerCase();
  var mime = String(file.type || '').toLowerCase();
  return (
    name.slice(-3) === '.gz' ||
    mime.indexOf('gzip') !== -1 ||
    mime === 'application/x-gzip'
  );
}

function readFileAsText(file) {
  if (!file) return Promise.resolve('');
  if (typeof file.text === 'function') {
    return file.text();
  }
  return new Promise(function (resolve, reject) {
    var reader = new FileReader();
    reader.onload = function (event) {
      resolve((event && event.target && event.target.result) || '');
    };
    reader.onerror = function () {
      reject(new Error('Could not read file.'));
    };
    reader.readAsText(file);
  });
}

function downloadBlob(blob, fileName) {
  var link = document.createElement('a');
  var objectUrl = window.URL.createObjectURL(blob);
  link.href = objectUrl;
  link.download = sanitizeFilename(fileName, 'network-tree-export.json');
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  setTimeout(function () {
    window.URL.revokeObjectURL(objectUrl);
    if (link.parentNode) {
      link.parentNode.removeChild(link);
    }
  }, 0);
}

function downloadTextAsJson(text, fileName) {
  var blob = new Blob([text], { type: 'application/json;charset=utf-8' });
  downloadBlob(blob, fileName);
}

function parseResponseError(res, text) {
  if (text) {
    try {
      var parsed = JSON.parse(text);
      if (parsed && (parsed.error || parsed.message)) {
        return parsed.error || parsed.message;
      }
    } catch (e) {}
    return text;
  }
  return 'Request failed (' + res.status + ')';
}

function openMessageModal(title, message) {
  modal.vm.title(title);
  modal.vm.body = function () {
    return m('div', [
      m('div.f5', message || 'Unknown error.')
    ]);
  };
  modal.vm.buttons([
    {
      label: 'Close',
      class: 'dib',
      func: function () {
        modal.vm.close();
      }
    }
  ]);
  modal.vm.open();
}

function openHelpAndReturn(helpTitle, helpFileName, reopenFn) {
  if (typeof reopenFn !== 'function') return;
  var reopenOnHelpClose = function () {
    loadingModal.off('closed', reopenOnHelpClose);
    reopenFn();
    m.redraw();
  };

  loadingModal.on('closed', reopenOnHelpClose);
  modal.vm.close();
  Promise.resolve(helpModal.editDialogue(helpTitle || 'Tree Transfer Help', helpFileName))
    .catch(function (error) {
      loadingModal.off('closed', reopenOnHelpClose);
      reopenFn();
      openMessageModal('Help Unavailable', (error && error.message) || 'Could not open help content.');
    });
}

function renderModalTitleWithHelp(titleText, helpTitle, helpFileName, reopenFn) {
  return m('span.flex.items-center', [
    m('span', titleText || ''),
    m('i.ion-ios-help-outline.white.f3.ml2.pointer', {
      title: 'Open help',
      onclick: function (ev) {
        if (ev) {
          ev.preventDefault();
          ev.stopPropagation();
        }
        openHelpAndReturn(helpTitle, helpFileName, reopenFn);
      }
    })
  ]);
}

function openExportPreviewModal(title, jsonText, downloadName) {
  modal.vm.title(
    renderModalTitleWithHelp(
      title,
      'Export JSON Help',
      EXPORT_HELP_FILE_NAME,
      function () {
        openExportPreviewModal(title, jsonText, downloadName);
      }
    )
  );
  modal.vm.body = function () {
    return m('div', [
      m('div.f6.mid-gray.mb2', 'Export size: ' + new Blob([jsonText]).size + ' bytes'),
      m('textarea.w-100.code.ba.b--mid-gray.pa2', {
        readonly: true,
        value: jsonText,
        style: {
          height: '60vh',
          resize: 'vertical'
        }
      })
    ]);
  };
  modal.vm.buttons([
    {
      label: 'Download',
      class: 'dib mr2',
      func: function () {
        downloadTextAsJson(jsonText, downloadName);
      }
    }
  ]);
  modal.vm.open({ extrawidth: true });
}

function requestExport(options) {
  var endpoint = options.asyncEndpoint || options.endpoint;
  var body = options.body || {};
  var previewTitle = options.previewTitle || 'Export Network Tree';
  var fallbackFileName = options.fileName || 'network-tree-export.json';

  loaders.transfer.start();
  if (loaders.transfer.setState) {
    loaders.transfer.setState('Exporting', 0);
  }
  m.redraw.sync();

  return new Promise(function (resolve) {
    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(resolve);
      return;
    }
    setTimeout(resolve, 0);
  })
    .then(function () {
      return fetch(endpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });
    })
    .then(function (res) {
      if (!res.ok) {
        return res.text().then(function (text) {
          throw new Error(parseResponseError(res, text));
        });
      }
      return res.text().then(function (text) {
        var parsed;
        try {
          parsed = text ? JSON.parse(text) : {};
        } catch (e) {
          parsed = null;
        }

        var uid = parsed && parsed.uid;
        if (!uid) {
          throw new Error('Export job did not return a uid.');
        }

        return new Promise(function (resolve, reject) {
          var timeoutId = setTimeout(function () {
            io.off(uid);
            reject(new Error('Export timed out while waiting for completion.'));
          }, 60 * 60 * 1000);

          io.off(uid);
          io.on(uid, function onTreeTransferExportEvent(data) {
            if (!data || data.uid !== uid) return;
            if (data.status === 'processing') {
              if (loaders.transfer.setState) {
                loaders.transfer.setState('Exporting', data.progressPct);
              }
              m.redraw();
              return;
            }
            if (data.status !== 'done' && data.status !== 'error') return;

            clearTimeout(timeoutId);
            io.off(uid);

            if (data.status === 'error') {
              reject(new Error(data.message || 'Export failed.'));
              return;
            }

            if (data.mode === 'download' && data.downloadUrl) {
              var downloadName = sanitizeFilename(data.downloadName, fallbackFileName);
              Promise.resolve(downloadFile(data.downloadUrl, { filename: downloadName }))
                .then(function () {
                  resolve({ mode: 'attachment' });
                })
                .catch(reject);
              return;
            }

            var previewText = String(data.payloadText || '');
            var formattedText = previewText;
            try {
              formattedText = JSON.stringify(JSON.parse(previewText), null, 2);
            } catch (e) {}
            openExportPreviewModal(
              previewTitle,
              formattedText,
              sanitizeFilename(data.downloadName, fallbackFileName)
            );
            resolve({ mode: 'preview' });
          });
        });
      });
    })
    .catch(function (error) {
      openMessageModal('Export Failed', (error && error.message) || 'Export failed.');
      throw error;
    })
    .finally(function () {
      loaders.transfer.stop();
      if (loaders.transfer.resetState) {
        loaders.transfer.resetState();
      }
      m.redraw();
    });
}

function openSelectionExportModal(config) {
  var items = (config.items || []).slice();
  var selectedByRid = {};
  var initialState = config.initialState || {};
  if (initialState.selectedByRid && typeof initialState.selectedByRid === 'object') {
    selectedByRid = Object.assign({}, initialState.selectedByRid);
  } else {
    items.forEach(function (item) {
      if (!item || !item.rid) return;
      selectedByRid[item.rid] = true;
    });
  }

  var state = {
    includeAll: typeof initialState.includeAll === 'boolean' ? initialState.includeAll : true,
    selectedByRid: selectedByRid,
    error: '',
    exporting: false,
    itemsToShow: [],
    listNewNode: prop(true),
    filteredDataLength: prop(null),
  };

  function getSelectedRids() {
    return Object.keys(state.selectedByRid).filter(function (rid) {
      return state.selectedByRid[rid];
    });
  }

  function setAllSelections(checked) {
    items.forEach(function (item) {
      if (!item || !item.rid) return;
      state.selectedByRid[item.rid] = !!checked;
    });
    state.includeAll = !!checked;
  }

  function submitExport() {
    if (state.exporting) {
      return Promise.resolve();
    }

    state.error = '';
    var selectedRids = getSelectedRids();
    if (!state.includeAll && selectedRids.length === 0) {
      state.error = 'Select at least one node to export or choose "Export all".';
      m.redraw();
      return Promise.reject(new Error(state.error));
    }

    state.exporting = true;
    modal.vm.hideCloseIcon(true);
    m.redraw();
    modal.vm.close();

    var requestBody = config.buildBody(state.includeAll, selectedRids);
    return requestExport({
      endpoint: config.endpoint,
      asyncEndpoint: config.asyncEndpoint,
      body: requestBody,
      previewTitle: config.previewTitle,
      fileName: config.fileName
    })
      .finally(function () {
        state.exporting = false;
        modal.vm.hideCloseIcon(false);
        m.redraw();
      });
  }

  modal.vm.title(
    renderModalTitleWithHelp(
      config.title || 'Export Network Tree',
      'Export Help',
      EXPORT_HELP_FILE_NAME,
      function () {
        openSelectionExportModal(Object.assign({}, config, {
          initialState: {
            includeAll: state.includeAll,
            selectedByRid: Object.assign({}, state.selectedByRid)
          }
        }));
      }
    )
  );
  modal.vm.body = function () {
    return m('div.relative', [
      state.error ? m('div.ba.b--light-red.bg-washed-red.dark-red.pa2.mb2.br2', state.error) : null,
      config.description ? m('div.mb2.f5', config.description) : null,
      m('div.flex.items-center.justify-between.mb2' + (state.exporting ? '' : '.pointer'), {
        onclick: function () {
          if (state.exporting) return;
          setAllSelections(!state.includeAll);
        }
      }, [
        m('span', 'Export all'),
        m('div', {
          onclick: function (ev) {
            ev.stopPropagation();
          }
        }, [
          m(ToggleSwitch, {
            checked: state.includeAll,
            disabled: state.exporting,
            onchange: function (ev) {
              setAllSelections(ev.target.checked);
            }
          })
        ])
      ]),
      m('div.ba.b--mid-gray.br2.pa2', {
        style: {
          opacity: state.includeAll ? 0.6 : 1,
          pointerEvents: state.includeAll || state.exporting ? 'none' : 'auto'
        }
      }, items.length
        ? m(paginationWithSearch, {
          data: items,
          newNode: state.listNewNode,
          dataToShow: state.itemsToShow,
          placeHolder: 'Search nodes',
          itemsPerPage: 12,
          fieldsToSearch: ['label', 'name', 'meta', 'rid'],
          filteredDataLength: state.filteredDataLength
        }, [
          m('div', state.itemsToShow
            .filter(function (item) {
              return item && item.rid;
            })
            .map(function (item) {
              return m('div.flex.items-center.justify-between.pointer.mb2', {
                id: 'tree-transfer-item-' + item.rid,
                onclick: function () {
                  state.selectedByRid[item.rid] = !state.selectedByRid[item.rid];
                }
              }, [
                m('div.flex.items-center', [
                  m('span', item.label || item.name || item.rid),
                  item.meta ? m('span.f6.mid-gray.ml2', item.meta) : null
                ]),
                m('div', {
                  onclick: function (ev) {
                    ev.stopPropagation();
                  }
                }, [
                  m(ToggleSwitch, {
                    checked: !!state.selectedByRid[item.rid],
                    onchange: function (ev) {
                      state.selectedByRid[item.rid] = ev.target.checked;
                    }
                  })
                ])
              ]);
            }))
        ])
        : [m('div.f5.mid-gray', config.emptyMessage || 'No nodes available for export.')])
    ]);
  };

  modal.vm.buttons([
    {
      label: 'Export',
      class: 'btn-good dib mr2',
      func: function () {
        return submitExport();
      }
    },
    {
      label: 'Close',
      class: 'dib',
      func: function () {
        if (state.exporting) return Promise.resolve();
        modal.vm.close();
      }
    }
  ]);
  modal.vm.open({ extrawidth: true });
}

function openImportModal(options) {
  var initialState = options.initialState || {};
  var state = {
    mode: initialState.mode || 'paste',
    payloadText: initialState.payloadText || '',
    uploadPayloadText: initialState.uploadPayloadText || '',
    uploadPayloadGzipBase64: initialState.uploadPayloadGzipBase64 || '',
    fileName: initialState.fileName || '',
    fileSize: Number(initialState.fileSize || 0),
    hidePayloadPreview: !!initialState.hidePayloadPreview,
    info: initialState.info || '',
    error: '',
    success: '',
    importing: false
  };

  var handleEscapeWhileImporting = function (event) {
    var isEscape = event && (event.key === 'Escape' || event.keyCode === 27);
    if (!state.importing || !isEscape) return;
    event.preventDefault();
    event.stopPropagation();
  };
  if (typeof window !== 'undefined' && window.addEventListener) {
    window.addEventListener('keydown', handleEscapeWhileImporting, true);
  }

  var cleanupOnClose = function () {
    if (typeof window !== 'undefined' && window.removeEventListener) {
      window.removeEventListener('keydown', handleEscapeWhileImporting, true);
    }
    modal.off('closed', cleanupOnClose);
  };
  modal.on('closed', cleanupOnClose);

  function setMode(mode) {
    if (state.importing) return;
    state.mode = mode;
    state.error = '';
  }

  function buildTemplateHref(download) {
    var endpoint = String(options.templateEndpoint || '').trim();
    if (!endpoint) return '';
    if (download === true) {
      return endpoint;
    }
    var glue = endpoint.indexOf('?') >= 0 ? '&' : '?';
    return endpoint + glue + 'download=0';
  }

  function buildPromptHref(download) {
    var endpoint = String(options.promptEndpoint || '').trim();
    if (!endpoint) return '';
    if (download === true) {
      return endpoint;
    }
    var glue = endpoint.indexOf('?') >= 0 ? '&' : '?';
    return endpoint + glue + 'download=0';
  }

  function readFile(file) {
    if (state.importing) return;
    state.error = '';
    state.success = '';
    state.info = '';
    if (!file) {
      state.fileName = '';
      state.payloadText = '';
      state.uploadPayloadText = '';
      state.uploadPayloadGzipBase64 = '';
      state.fileSize = 0;
      state.hidePayloadPreview = false;
      state.info = '';
      return;
    }

    var gzipUpload = isGzipFile(file);
    var sizeError = importLimits.validateByteLength(file.size, gzipUpload);
    if (sizeError) {
      state.error = sizeError.message;
      state.fileName = file.name || '';
      state.fileSize = Number(file.size || 0);
      state.payloadText = '';
      state.uploadPayloadText = '';
      state.uploadPayloadGzipBase64 = '';
      state.hidePayloadPreview = false;
      m.redraw();
      return Promise.resolve(null);
    }
    var readPromise = gzipUpload
      ? readFileAsBase64(file)
      : readFileAsText(file);

    return readPromise.then(function (content) {
      state.fileName = file.name || '';
      state.fileSize = Number(file.size || 0);
      if (gzipUpload) {
        state.uploadPayloadGzipBase64 = content;
        state.uploadPayloadText = '';
        state.payloadText = '';
        state.hidePayloadPreview = true;
        state.info = '';
        m.redraw();
        return null;
      }

      var text = content;
      state.uploadPayloadGzipBase64 = '';
      state.uploadPayloadText = text;
      state.hidePayloadPreview = state.fileSize > MAX_INLINE_PAYLOAD_PREVIEW_BYTES;
      if (state.hidePayloadPreview) {
        state.payloadText = '';
      } else {
        state.payloadText = text;
      }
      state.info = '';
      m.redraw();
      return null;
    }).catch(function (error) {
      state.error = 'Could not read file.';
      if (error && error.message) {
        state.error = error.message;
      }
      m.redraw();
      return null;
    });
  }

  function submitImport() {
    if (state.importing) {
      return Promise.resolve();
    }

    state.error = '';
    state.success = '';
    var isCompressedUpload = state.mode === 'upload' && !!state.uploadPayloadGzipBase64;
    var payloadSource = (state.mode === 'upload' && state.hidePayloadPreview)
      ? state.uploadPayloadText
      : state.payloadText;
    var payloadText = String(payloadSource || '').trim();
    if (!isCompressedUpload && !payloadText) {
      state.error = 'Paste JSON or upload a .json/.gz file before importing.';
      m.redraw();
      return Promise.reject(new Error(state.error));
    }
    if (!isCompressedUpload) {
      var scopeValidationError = validateImportBundleScope(payloadText, options);
      if (scopeValidationError) {
        state.error = scopeValidationError;
        m.redraw();
        return Promise.reject(new Error(scopeValidationError));
      }
    }

    state.importing = true;
    modal.vm.hideCloseIcon(true);
    m.redraw();

    var requestBodyPromise = isCompressedUpload
      ? Promise.resolve({
          body: {
            payloadEncoding: 'gzip-base64',
            payloadGzipBase64: state.uploadPayloadGzipBase64
          },
          compressed: true
        })
      : buildImportRequestBody(payloadText);

    var usesTransferLoader = false;
    var asyncModalClosed = false;

    return requestBodyPromise
      .then(function (requestBodyInfo) {
        var useAsyncEndpoint = !!options.asyncEndpoint;
        var targetEndpoint = useAsyncEndpoint ? options.asyncEndpoint : options.endpoint;
        state.info = requestBodyInfo.compressed ? '' : state.info;
        if (useAsyncEndpoint) {
          usesTransferLoader = true;
          loaders.transfer.start();
          if (loaders.transfer.setState) {
            loaders.transfer.setState('Importing', 0);
          }
          if (loaders.transfer.setCancelable) {
            loaders.transfer.setCancelable('', options.cancelEndpoint || '/api/v1/network/tree-transfer/import-cancel');
          }
          asyncModalClosed = true;
          modal.vm.close();
          m.redraw();
        }
        return fetch(targetEndpoint, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(requestBodyInfo.body)
        }).then(function (res) {
          return {
            res: res,
            useAsyncEndpoint: useAsyncEndpoint
          };
        });
      })
      .then(function (resultInfo) {
        var res = resultInfo.res;
        var useAsyncEndpoint = !!resultInfo.useAsyncEndpoint;
        return res.text().then(function (text) {
          var parsed;
          try {
            parsed = text ? JSON.parse(text) : {};
          } catch (e) {
            parsed = null;
          }

          if (!res.ok) {
            var message = parsed && (parsed.error || parsed.message)
              ? (parsed.error || parsed.message)
              : parseResponseError(res, text);
            throw new Error(message);
          }

          if (useAsyncEndpoint) {
            var uid = parsed && parsed.uid;
            if (!uid) {
              throw new Error('Import job did not return a uid.');
            }
            if (loaders.transfer.setCancelable) {
              loaders.transfer.setCancelable(uid, options.cancelEndpoint || '/api/v1/network/tree-transfer/import-cancel');
            }

            state.success = (parsed && parsed.message) || 'Import started in background.';
            return new Promise(function (resolve, reject) {
              var finalized = false;
              function finalizeTransferLoader(opts) {
                if (!usesTransferLoader || finalized) return;
                finalized = true;
                var delayMs = opts && Number.isFinite(Number(opts.delayMs)) ? Number(opts.delayMs) : 0;
                var label = opts && typeof opts.label === 'string' ? opts.label : '';
                var progress = opts && Number.isFinite(Number(opts.progress)) ? Number(opts.progress) : null;
                if (label && loaders.transfer.setState) {
                  loaders.transfer.setState(label, progress);
                  m.redraw();
                }
                setTimeout(function () {
                  loaders.transfer.stop();
                  if (loaders.transfer.resetState) {
                    loaders.transfer.resetState();
                  }
                  usesTransferLoader = false;
                  m.redraw();
                }, delayMs);
              }

              var timeoutId = setTimeout(function () {
                io.off(uid);
                finalizeTransferLoader();
                reject(new Error('Import timed out while waiting for completion.'));
              }, 60 * 60 * 1000);

              io.off(uid);
              io.on(uid, function onTreeTransferJobStatus(data) {
                if (!data || data.uid !== uid) return;
                if (data.status === 'processing') {
                  var label = data && /cancel/i.test(String(data.message || ''))
                    ? 'Cancelling'
                    : 'Importing';
                  if (loaders.transfer.setState) {
                    loaders.transfer.setState(label, data.progressPct);
                  }
                  m.redraw();
                  return;
                }
                if (data.status !== 'done' && data.status !== 'error') return;

                clearTimeout(timeoutId);
                io.off(uid);

                if (data.status === 'error' && data.cancelled) {
                  finalizeTransferLoader({
                    label: 'Canceled',
                    progress: 100,
                    delayMs: 2000
                  });
                  resolve(null);
                  return;
                }

                if (data.status === 'done') {
                  finalizeTransferLoader();
                  openMessageModal('Import Completed', data.message || 'Import completed.');
                  if (typeof options.onSuccess === 'function') {
                    Promise.resolve(options.onSuccess())
                      .then(function () {
                        m.redraw();
                        resolve(null);
                      })
                      .catch(reject);
                    return;
                  }
                  m.redraw();
                  resolve(null);
                  return;
                }

                finalizeTransferLoader();
                reject(new Error(data.message || 'Import failed.'));
              });
            });
          }

          state.success = (parsed && (parsed.message || parsed.status)) || 'Import completed.';
          if (typeof options.onSuccess === 'function') {
            return Promise.resolve(options.onSuccess()).then(function () {
              m.redraw();
            });
          }
          m.redraw();
          return null;
        });
      })
      .catch(function (error) {
        if (usesTransferLoader) {
          loaders.transfer.stop();
          if (loaders.transfer.resetState) {
            loaders.transfer.resetState();
          }
          usesTransferLoader = false;
        }
        var errorMessage = (error && error.message) || 'Import failed.';
        if (asyncModalClosed) {
          openMessageModal('Import Failed', errorMessage);
        } else {
          state.error = errorMessage;
          m.redraw();
        }
        throw error;
      })
      .finally(function () {
        state.importing = false;
        modal.vm.hideCloseIcon(false);
        if (usesTransferLoader) {
          loaders.transfer.stop();
          if (loaders.transfer.resetState) {
            loaders.transfer.resetState();
          }
        }
        m.redraw();
      });
  }

  modal.vm.title(
    renderModalTitleWithHelp(
      options.title || 'Import Network Tree',
      'Import Help',
      IMPORT_HELP_FILE_NAME,
      function () {
        openImportModal(Object.assign({}, options, {
          initialState: {
            mode: state.mode,
            payloadText: state.payloadText,
            uploadPayloadText: state.uploadPayloadText,
            uploadPayloadGzipBase64: state.uploadPayloadGzipBase64,
            fileName: state.fileName,
            fileSize: state.fileSize,
            hidePayloadPreview: state.hidePayloadPreview,
            info: state.info
          }
        }));
      }
    )
  );
  modal.vm.body = function () {
    return m('div.relative', [
      m('div', {
        style: {
          opacity: state.importing ? 0.45 : 1,
          pointerEvents: state.importing ? 'none' : 'auto'
        }
      }, [
        state.error ? m('div.ba.b--light-red.bg-washed-red.dark-red.pa2.mb2.br2', state.error) : null,
        state.success ? m('div.ba.b--light-green.bg-washed-green.dark-green.pa2.mb2.br2', state.success) : null,
        state.info ? m('div.ba.b--light-blue.bg-washed-blue.dark-blue.pa2.mb2.br2', state.info) : null,
        options.templateEndpoint
          ? m('div.mb3', [
              m('div.mb1.white', options.templatePrompt || 'Need the expected template format?'),
              m('div.flex', [
                m('a.pointer.ba.b--mid-gray.ph2.pv1.mr2.br2.near-white.link', {
                  href: buildTemplateHref(false),
                  target: '_blank',
                  rel: 'noopener noreferrer'
                }, 'View Template'),
                m('a.pointer.ba.b--mid-gray.ph2.pv1.br2.near-white.link', {
                  href: buildTemplateHref(true),
                  target: '_blank',
                  rel: 'noopener noreferrer'
                }, 'Download Template')
              ])
            ])
          : null,
        options.promptEndpoint
          ? m('div.mb3', [
              m('div.mb1.white', options.promptPrompt || 'If you have network data in an Excel file, you can convert it into Nimrod valid JSON using this prompt.'),
              m('div.flex', [
                m('a.pointer.ba.b--mid-gray.ph2.pv1.mr2.br2.near-white.link', {
                  href: buildPromptHref(false),
                  target: '_blank',
                  rel: 'noopener noreferrer'
                }, 'View Prompt'),
                m('a.pointer.ba.b--mid-gray.ph2.pv1.br2.near-white.link', {
                  href: buildPromptHref(true),
                  target: '_blank',
                  rel: 'noopener noreferrer'
                }, 'Download Prompt')
              ])
            ])
          : null,
        m('div.f6.white.mb1.mt2.pt1', 'Import source'),
        m('div.flex.mb3', [
          m('div.pointer.ba.b--mid-gray.ph2.pv1.mr2.br2' + (state.mode === 'paste' ? '.bg-mid-gray.near-white' : ''), {
            onclick: function () { setMode('paste'); }
          }, 'Paste JSON'),
          m('div.pointer.ba.b--mid-gray.ph2.pv1.br2' + (state.mode === 'upload' ? '.bg-mid-gray.near-white' : ''), {
            onclick: function () { setMode('upload'); }
          }, 'Upload File')
        ]),
        state.mode === 'upload' ? m('div.mb2', [
          m(fileInput, {
            field: {
              id: 'network-tree-import',
              name: 'JSON File',
              accept: '.json,.gz,.json.gz'
            },
            onChange: readFile
          }),
          m('div.f6.mid-gray', state.fileName || 'No file selected')
        ]) : null,
        (state.mode === 'upload' && state.hidePayloadPreview)
          ? null
          : [
              m('label.db.mb1', 'JSON Payload'),
              m('textarea.w-100.code.ba.b--mid-gray.pa2', {
                value: state.payloadText,
                oninput: function (ev) {
                  state.payloadText = ev.target.value;
                },
                placeholder: options.placeholder || 'Paste exported network tree JSON here',
                style: {
                  height: '45vh',
                  resize: 'vertical'
                }
              })
            ]
      ]),
      state.importing ? m('div.absolute.top-0.left-0.right-0.bottom-0.flex.items-center.justify-center', {
        style: {
          background: 'rgba(0, 0, 0, 0.35)',
          zIndex: 2
        }
      }, [
        m('div.br2.bg-near-black.ph3.pv2.flex.items-center', [
          m('i.f2.mr2.ion-load-a.spin'),
          m('span.f5', 'Importing network tree...')
        ])
      ]) : null
    ]);
  };

  modal.vm.buttons([
    {
      label: 'Import',
      class: 'btn-good dib mr2',
      func: function () {
        return submitImport();
      }
    }
  ]);

  modal.vm.open({ extrawidth: true });
}

function toHostExportItems(hostRecords) {
  return (hostRecords || [])
    .filter(function (record) {
      return !!getHostSpec(record && record['@class']);
    })
    .map(function (record) {
      var spec = getHostSpec(record['@class']);
      var rid = record['@rid'];
      return {
        rid: rid,
        label: record.name || rid,
        meta: '(' + spec.label + ')'
      };
    });
}

function toChildExportItems(hostRecord, childRecords) {
  var spec = getHostSpec(hostRecord && hostRecord['@class']);
  if (!spec) return [];
  return (childRecords || [])
    .filter(function (record) {
      return record && record['@class'] === spec.childClassName;
    })
    .map(function (record) {
      return {
        rid: record['@rid'],
        label: record.name || record['@rid'],
        meta: '(' + spec.childLabel + ')'
      };
    });
}

function trimRid(rid) {
  return String(rid || '').replace(/^#/, '');
}

treeTransferModal.openNetworkExportModal = function (hostRecords) {
  var items = toHostExportItems(hostRecords);
  openSelectionExportModal({
    title: 'Export Network Trees',
    description: 'Export multiple hosts across BACnet, Modbus, LonTalk and REST in one JSON file.',
    endpoint: '/api/v1/network/tree-transfer/export',
    asyncEndpoint: '/api/v1/network/tree-transfer/export-async',
    previewTitle: 'Export Network Trees',
    fileName: 'network-tree-export.json',
    emptyMessage: 'No supported network hosts are available.',
    items: items,
    buildBody: function (includeAll, selectedRids) {
      return {
        includeAll: includeAll,
        hostRids: selectedRids.map(trimRid)
      };
    }
  });
};

treeTransferModal.openNetworkImportModal = function (onSuccess) {
  openImportModal({
    title: 'Import Network Trees',
    endpoint: '/api/v1/network/tree-transfer/import',
    asyncEndpoint: '/api/v1/network/tree-transfer/import-async',
    cancelEndpoint: '/api/v1/network/tree-transfer/import-cancel',
    templateEndpoint: '/api/v1/network/tree-transfer/import-template',
    templatePrompt: 'Need the expected network bundle format?',
    promptEndpoint: '/api/v1/network/tree-transfer/import-prompt',
    promptPrompt: 'If you have network data in an Excel file, you can convert it into Nimrod valid JSON using this prompt.',
    expectedBundleScopes: ['network'],
    placeholder: 'Paste exported network-tree bundle JSON here',
    onSuccess: onSuccess
  });
};

treeTransferModal.openHostExportModal = function (hostRecord, childRecords) {
  var spec = getHostSpec(hostRecord && hostRecord['@class']);
  if (!hostRecord || !hostRecord['@rid'] || !spec) {
    openMessageModal('Export Nodes', 'Selected host is not supported for tree export.');
    return;
  }

  var items = toChildExportItems(hostRecord, childRecords);
  openSelectionExportModal({
    title: 'Export ' + spec.childLabel + ' Nodes',
    description: 'Export multiple ' + spec.childLabel + ' nodes from this host in one JSON file.',
    endpoint: '/api/v1/host/' + trimRid(hostRecord['@rid']) + '/tree-transfer/export',
    asyncEndpoint: '/api/v1/host/' + trimRid(hostRecord['@rid']) + '/tree-transfer/export-async',
    previewTitle: 'Export Host Nodes',
    fileName: 'host-tree-export-' + (hostRecord.name || trimRid(hostRecord['@rid'])) + '.json',
    emptyMessage: 'No ' + spec.childLabel + ' nodes are available under this host.',
    items: items,
    buildBody: function (includeAll, selectedRids) {
      return {
        includeAll: includeAll,
        childRids: selectedRids.map(trimRid)
      };
    }
  });
};

treeTransferModal.openHostImportModal = function (hostRecord, onSuccess) {
  if (!hostRecord || !hostRecord['@rid'] || !getHostSpec(hostRecord['@class'])) {
    openMessageModal('Import Nodes', 'Selected host is not supported for tree import.');
    return;
  }

  openImportModal({
    title: 'Import Host Nodes',
    endpoint: '/api/v1/host/' + trimRid(hostRecord['@rid']) + '/tree-transfer/import',
    asyncEndpoint: '/api/v1/host/' + trimRid(hostRecord['@rid']) + '/tree-transfer/import-async',
    cancelEndpoint: '/api/v1/network/tree-transfer/import-cancel',
    templateEndpoint: '/api/v1/host/' + trimRid(hostRecord['@rid']) + '/tree-transfer/import-template',
    templatePrompt: 'Need the expected host import format?',
    expectedBundleScopes: ['host', 'network'],
    placeholder: 'Paste exported host-node bundle JSON or compatible legacy JSON here',
    onSuccess: onSuccess
  });
};

module.exports = treeTransferModal;
